/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Logo from '@/components/Logo';
import { toast } from 'sonner';
import { verifyMagicLink } from '@/lib/magicLink';
import { useAuth } from '@/lib/auth';
import { Loader2, CheckCircle2, XCircle, ArrowRight } from 'lucide-react';

/**
 * Handles the /auth/magic?token=... callback.
 * Consumes the token → backend sets sn_auth httpOnly cookie → we refresh auth
 * context and redirect the user to their home page.
 */
const MagicLinkCallbackPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [state, setState] = useState('loading'); // loading | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const [user, setUser] = useState(null);
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;
    const token = params.get('token');
    if (!token) {
      setState('error');
      setErrorMsg('Link non valido: token mancante.');
      return;
    }

    (async () => {
      try {
        const res = await verifyMagicLink(token);
        if (res?.user) setUser(res.user);
        // Refresh AuthContext so the whole app knows we're logged in.
        // We swallow the error here on purpose: even if refreshUser() fails,
        // the sn_auth cookie is already set and the next navigation will re-hydrate
        // the auth state. We still log at debug level for observability.
        try {
          await refreshUser?.();
        } catch (refreshErr) {
          if (process.env.NODE_ENV !== 'production') {
            console.debug('[MagicLink] refreshUser after verify failed (non-fatal):', refreshErr);
          }
        }
        setState('success');
        toast.success('Accesso completato!');
        // Small delay for visual feedback, then redirect
        setTimeout(() => {
          const isPatient = res?.user?.role === 'patient' || res?.user?.category === 'paziente';
          // Il backend può indicare una destinazione interna (es. /portale?report=<id> per un referto approvato).
          const redirect = typeof res?.redirect === 'string' && res.redirect.startsWith('/') && res.redirect !== '/' ? res.redirect : null;
          navigate(redirect || (isPatient ? '/telemedicine' : '/feed'), { replace: true });
        }, 900);
      } catch (err) {
        const msg = err?.response?.data?.detail || 'Link non valido o scaduto';
        setState('error');
        setErrorMsg(msg);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-background bg-hero-gradient flex items-center justify-center px-4 py-8 relative">
      <div className="absolute inset-0 bg-grain opacity-30 pointer-events-none" />
      <div className="w-full max-w-md relative">
        <div className="mb-6">
          <Logo size={44} withText textClassName="text-base" />
        </div>
        <Card className="p-8 rounded-2xl shadow-elev-lg text-center" data-testid="magic-link-callback-card">
          {state === 'loading' && (
            <div data-testid="magic-link-callback-loading">
              <Loader2 className="h-10 w-10 text-primary mx-auto animate-spin mb-4" />
              <h1 className="font-display font-semibold text-lg">Verifica in corso…</h1>
              <p className="text-sm text-muted-foreground mt-2">Sto validando il tuo link di accesso.</p>
            </div>
          )}
          {state === 'success' && (
            <div data-testid="magic-link-callback-success">
              <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                <CheckCircle2 className="h-7 w-7 text-emerald-600" />
              </div>
              <h1 className="font-display font-semibold text-lg">Accesso riuscito</h1>
              <p className="text-sm text-muted-foreground mt-2">
                {user?.full_name ? `Bentornato, ${user.full_name}` : 'Ti stiamo reindirizzando…'}
              </p>
              <div className="mt-4 inline-flex items-center gap-1 text-primary text-sm font-medium">
                Portami in app <ArrowRight className="h-4 w-4" />
              </div>
            </div>
          )}
          {state === 'error' && (
            <div data-testid="magic-link-callback-error">
              <div className="mx-auto w-14 h-14 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
                <XCircle className="h-7 w-7 text-destructive" />
              </div>
              <h1 className="font-display font-semibold text-lg">Impossibile completare l'accesso</h1>
              <p className="text-sm text-muted-foreground mt-2">{errorMsg}</p>
              <div className="mt-6 flex flex-col gap-2">
                <Button onClick={() => navigate('/auth/magic-link')} data-testid="magic-link-callback-retry-btn">
                  Richiedi un nuovo link
                </Button>
                <Button variant="ghost" onClick={() => navigate('/login')} data-testid="magic-link-callback-login-btn">
                  Accedi con password
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default MagicLinkCallbackPage;
